import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import Button from "../../../shared/Button/Button";
import Select from "../../../shared/Select/Select";
import {
  formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import {
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import {
  type SportsbookDailyFilterOption,
  type SportsbookDailyFilterOptions,
  useGetSportsbookDailyPerformance,
} from "../../../../queries/sportsbook-kpis/useGetSportsbookDailyPerformance";
import { BarChart, ChartCard, CHART_PALETTE } from "../../../shared/Charts";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import {
  createDefaultSportsbookKpiFilterOptions,
  SPORTSBOOK_KPI_ALL_FILTER_VALUE,
} from "../../../../queries/sportsbook-kpis/sportsbookKpiFilters";

import styles from "./DailyPerformance.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

// Turnover and P&L are both currency series on a single axis.
const SERIES = [
  { key: "turnover", color: CHART_PALETTE.seriesGreen },
  { key: "pnl", color: CHART_PALETTE.seriesPurple },
] as const;

const SERIES_LABELS: Record<(typeof SERIES)[number]["key"], string> = {
  turnover: AppStrings.TURNOVER,
  pnl: AppStrings.PROFIT_LOSS,
};

const DailyPerformance: React.FC = () => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.subtract(30, "day").startOf("day"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.LAST_30_DAYS),
  );
  const [sport, setSport] = useState(SPORTSBOOK_KPI_ALL_FILTER_VALUE);
  const [league, setLeague] = useState(SPORTSBOOK_KPI_ALL_FILTER_VALUE);
  const [betTypes, setBetTypes] = useState<string[]>([]);
  const [moneyTypes, setMoneyTypes] = useState<string[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const seededFilters = useRef(false);

  const currencySymbol = useCurrencySymbol() ?? "$";

  // Date / filter changes drive the query key so the hook refetches itself.
  const queryFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
      sport,
      league,
      bet_type: betTypes,
      money_type: moneyTypes,
    }),
    [betTypes, dates, league, moneyTypes, sport],
  );

  const { data, isFetching, isLoading, isError } =
    useGetSportsbookDailyPerformance(queryFilters);

  const showLoading = isLoading || isFetching;

  const points = useMemo(() => data?.points ?? [], [data?.points]);

  // Sport catalog comes from the BE; until the first response lands we
  // show just the "All Sports" placeholder so the dropdown is always
  // operable.
  const liveFilterOptions: SportsbookDailyFilterOptions | undefined =
    data?.filterOptions;
  const defaultFilterOptions = useMemo(
    () => createDefaultSportsbookKpiFilterOptions(),
    [],
  );
  const sportOptions = useMemo<SportsbookDailyFilterOption[]>(
    () => liveFilterOptions?.sports ?? defaultFilterOptions.sports,
    [defaultFilterOptions.sports, liveFilterOptions?.sports],
  );
  const leagueOptions = useMemo<SportsbookDailyFilterOption[]>(
    () => liveFilterOptions?.leagues ?? defaultFilterOptions.leagues,
    [defaultFilterOptions.leagues, liveFilterOptions?.leagues],
  );
  const betTypeOptions = useMemo<SportsbookDailyFilterOption[]>(
    () => liveFilterOptions?.betTypes ?? defaultFilterOptions.betTypes,
    [defaultFilterOptions.betTypes, liveFilterOptions?.betTypes],
  );
  const currencyTypeOptions = useMemo<SportsbookDailyFilterOption[]>(
    () =>
      liveFilterOptions?.currencyTypes ?? defaultFilterOptions.currencyTypes,
    [defaultFilterOptions.currencyTypes, liveFilterOptions?.currencyTypes],
  );

  useEffect(() => {
    if (seededFilters.current || !data?.selectedFilters) return;
    seededFilters.current = true;
    setSport(data.selectedFilters.sport);
    setLeague(data.selectedFilters.league);
    setBetTypes([]);
    setMoneyTypes([]);
  }, [data?.selectedFilters]);

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query key, which refetches.
  }, []);

  // Reset synchronously so no render can request a league from the old sport.
  const handleSportChange = useCallback((next: string) => {
    setSport(next);
    setLeague(SPORTSBOOK_KPI_ALL_FILTER_VALUE);
  }, []);

  const isLeagueDisabled =
    sport === SPORTSBOOK_KPI_ALL_FILTER_VALUE || isFetching;

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.date, "MMM D")),
      datasets: SERIES.map((s) => ({
        label: SERIES_LABELS[s.key],
        data: points.map((p) => p[s.key]),
        backgroundColor: s.color,
        borderRadius: 3,
        // barPercentage 1 + no maxBarThickness → Turnover/P&L bars touch per day.
        barPercentage: 1.0,
        categoryPercentage: 0.7,
      })),
    }),
    [points],
  );

  // Only the bits specific to this chart — the rest comes from the shared theme.
  const chartOptions = useMemo(
    () => ({
      datasets: {
        bar: {
          barPercentage: 1.0,
          categoryPercentage: 0.7,
        },
      },
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: number | string) =>
              formatCompactCurrencyTick(Number(value), currencySymbol),
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "index" as const,
          intersect: false,
          displayColors: true,
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              if (!items?.length) return "";
              const iso = points[items[0].dataIndex]?.date;
              return iso ? formatDateNoTimezone(iso, "MMM D, YYYY") : "";
            },
            // Append the series label after the value so the tooltip reads
            // e.g. "$667 Turnover" / "$578 Profit & Loss" instead of two
            // bare currency figures stacked on top of each other.
            label: (item: { raw: unknown; datasetIndex: number }) => {
              const series = SERIES[item.datasetIndex];
              const value = formatCurrency(Number(item.raw), currencySymbol);
              const label = series ? SERIES_LABELS[series.key] : "";
              return label ? `${value} ${label}` : value;
            },
          },
        },
      },
    }),
    [points, currencySymbol],
  );

  const exportRows = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        sport,
        league,
        bet_type: betTypes.join(","),
        money_type: moneyTypes.join(","),
        turnover: p.turnover,
        profit_and_loss: p.pnl,
      })),
    [betTypes, league, moneyTypes, points, sport],
  );

  const headerContent = (
    <div className={styles.headerLeft}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>{AppStrings.DAILY_PERFORMANCE}</h2>
        <span className={styles.filterToggle}>
          <Button
            onClick={() => setFiltersVisible((visible) => !visible)}
            aria-expanded={filtersVisible}
            aria-controls="sportsbook-daily-filters"
            aria-label={AppStrings.PAM_REPORT_FILTERS}
          >
            <Filter size={18} aria-hidden="true" />
            {filtersVisible ? (
              <ChevronUp size={16} aria-hidden="true" />
            ) : (
              <ChevronDown size={16} aria-hidden="true" />
            )}
          </Button>
        </span>
        <div
          id="sportsbook-daily-filters"
          className={`${styles.filters}${
            filtersVisible ? "" : ` ${styles.filtersCollapsed}`
          }`}
          data-testid="sportsbook-daily-filters"
        >
          <Select
            className={styles.filterSelect}
            value={sport}
            options={sportOptions}
            onChange={handleSportChange}
            aria-label={AppStrings.SPORT}
            showSearch
            optionFilterProp="label"
            loading={showLoading}
            data-testid="sportsbook-daily-sport-select"
          />
          <Select
            className={styles.filterSelect}
            value={league}
            options={leagueOptions}
            onChange={setLeague}
            aria-label={AppStrings.LEAGUE}
            showSearch
            optionFilterProp="label"
            disabled={isLeagueDisabled}
            loading={showLoading}
            placeholder={AppStrings.SELECT_A_SPORT_FIRST}
            data-testid="sportsbook-daily-league-select"
          />
          <Select
            mode="multiple"
            className={styles.filterSelect}
            value={betTypes}
            options={betTypeOptions}
            onChange={setBetTypes}
            aria-label={AppStrings.BET_TYPE}
            placeholder={AppStrings.SPORTSBOOK_KPI_ALL_BET_TYPES}
            showSearch
            optionFilterProp="label"
            disabled={!data?.availableFilters?.bet_type}
            loading={showLoading}
            maxTagCount="responsive"
            data-testid="sportsbook-daily-bet-type-select"
          />
          <Select
            mode="multiple"
            className={styles.filterSelect}
            value={moneyTypes}
            options={currencyTypeOptions}
            onChange={setMoneyTypes}
            aria-label={AppStrings.SPORTSBOOK_PROFITABILITY_CURRENCY_TYPE}
            placeholder={AppStrings.SPORTSBOOK_KPI_ALL_CURRENCY_TYPES}
            showSearch
            optionFilterProp="label"
            loading={showLoading}
            maxTagCount="responsive"
            data-testid="sportsbook-daily-currency-type-select"
          />
        </div>
      </div>
      <ul className={styles.legend}>
        {SERIES.map((s) => (
          <li key={s.key} className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ backgroundColor: s.color }}
              aria-hidden="true"
            />
            {SERIES_LABELS[s.key]}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <ChartCard
      ariaLabel={AppStrings.DAILY_PERFORMANCE}
      headerLayout="row"
      headerExtra={headerContent}
      bodyClassName={styles.chartContainer}
      controls={
        <>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="sportsbook-daily-performance.xlsx"
          />
        </>
      }
    >
      {isError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="daily-performance-error"
        >
          {AppStrings.SPORTSBOOK_DAILY_LOAD_ERROR}
        </p>
      ) : points.length === 0 && !showLoading ? (
        <p className={styles.emptyMessage} data-testid="daily-performance-empty">
          {AppStrings.PAM_CHART_EMPTY_STATE}
        </p>
      ) : (
        <BarChart
          data={chartData}
          optionsOverride={chartOptions}
          isLoading={showLoading}
        />
      )}
    </ChartCard>
  );
};

export default DailyPerformance;
